import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './App.css';
import { AppStoreProvider, useAppStore } from './state/AppStore';
import { blockRegistry } from './components/blocks/registry';
import HistoryDisplay from './components/HistoryDisplay';
import SummaryScreen from './components/SummaryScreen';
import Sidebar from './components/Sidebar';
import AIAssistant from './components/AIAssistant';

// Бургер-меню для мобильных устройств.
function BurgerMenu({ open, onToggle }) {
  return (
    <button className="burger-menu-button" onClick={onToggle} aria-label="Открыть меню">
      <span className={`burger-line ${open ? 'open' : ''}`}></span>
      <span className={`burger-line ${open ? 'open' : ''}`}></span>
      <span className={`burger-line ${open ? 'open' : ''}`}></span>
    </button>
  );
}

function AppContent() {
  const navigate = useNavigate();
  const { state, actions, currentBlock, error } = useAppStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const screenRef = useRef(null);
  const didInitialScroll = useRef(false);

  // Прокрутка к текущему экрану (но не на экране итогов).
  // Зависит и от state.config: при перезагрузке конфиг приходит асинхронно, и без
  // этого экран не прокручивался бы к текущему (оставался бы вверху, на истории).
  useEffect(() => {
    if (state.showSummary || !state.config) return;
    const el = screenRef.current;
    if (!el) return;
    // Первый показ (после загрузки конфига/перезагрузки) — без анимации, сразу к экрану;
    // последующие переходы — плавно.
    const behavior = didInitialScroll.current ? 'smooth' : 'auto';
    didInitialScroll.current = true;
    requestAnimationFrame(() => {
      if (screenRef.current) {
        screenRef.current.scrollIntoView({ behavior, block: 'start' });
      }
    });
  }, [state.currentBlockId, state.showSummary, state.config]);

  const handleExit = () => navigate('/');

  const sidebar = (
    <Sidebar
      variables={state.variables}
      onPhraseSelect={actions.selectPhrase}
      viewingPhraseIndex={state.viewingPhraseIndex}
      onRestart={actions.restart}
      onExit={handleExit}
      isOpen={sidebarOpen}
      onClose={() => setSidebarOpen(false)}
    />
  );

  // Загрузка конфигурации.
  if (!state.config && !error) {
    return (
      <div className="App">
        <div className="app-main-content">
          <div className="screen">
            <div className="screen-content">
              <h1 className="large-text">Загрузка...</h1>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Ошибка загрузки.
  if (error || !state.config) {
    return (
      <div className="App">
        <div className="app-main-content">
          <div className="screen">
            <div className="screen-content">
              <h1 className="large-text">Ошибка загрузки конфигурации</h1>
              <p>{error || 'Не удалось загрузить конфигурацию'}</p>
              <button className="button" onClick={() => window.location.reload()}>
                Перезагрузить
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Экран итогов.
  if (state.showSummary || (currentBlock && currentBlock.id === 'summary')) {
    return (
      <div className="App">
        <div className="app-main-content">
          <SummaryScreen
            history={state.history}
            variables={state.variables}
            onRestart={actions.restart}
            onNextPhrase={actions.nextPhrase}
            viewingPhraseIndex={state.viewingPhraseIndex}
            onPhraseSelect={actions.selectPhrase}
          />
        </div>
        <BurgerMenu open={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
        {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}
        {sidebar}
        <AIAssistant />
      </div>
    );
  }

  // Блок не найден.
  if (!currentBlock) {
    return (
      <div className="App">
        <div className="app-main-content">
          <div className="screen">
            <div className="screen-content">
              <h1>Ошибка: блок не найден</h1>
              <button className="button" onClick={actions.restart}>
                Начать заново
              </button>
            </div>
          </div>
        </div>
        {sidebar}
        <AIAssistant />
      </div>
    );
  }

  const BlockComponent = blockRegistry[currentBlock.type];
  const onNext = currentBlock.type === 'condition' ? actions.conditionNext : actions.blockNext;

  return (
    <div className="App">
      <div className="app-main-content">
        {/* История пройденных экранов */}
        <HistoryDisplay
          history={state.history}
          variables={state.variables}
          viewingPhraseIndex={state.viewingPhraseIndex}
          onPhraseSelect={actions.selectPhrase}
        />

        {/* Текущий экран */}
        <div ref={screenRef} className="screen-wrapper">
          {BlockComponent ? (
            <BlockComponent block={currentBlock} variables={state.variables} onNext={onNext} />
          ) : (
            <div className="screen">
              <div className="screen-content">
                <h1>Неизвестный тип блока: {currentBlock.type}</h1>
              </div>
            </div>
          )}
        </div>
      </div>

      <BurgerMenu open={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}
      {sidebar}
      <AIAssistant />
    </div>
  );
}

function App() {
  return (
    <AppStoreProvider>
      <AppContent />
    </AppStoreProvider>
  );
}

export default App;
